import { useCallback, useEffect, useRef, useState } from "react";
import { Phone, MessageCircle, Mail, FileText, RefreshCw, LogOut, Volume2, VolumeX, Loader2, ShieldCheck, ShieldAlert, ShieldQuestion, Keyboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SEO from "@/components/SEO";
import { adminPing, fetchLeadsAdmin, updateLeadStatus } from "@/lib/api";

const KEY_STORAGE = "carfornia_admin_key";
const REFRESH_MS = 30000;
const STATUSES = [
  { id: "new", label: "New", cls: "bg-brand-orange text-white" },
  { id: "contacted", label: "Contacted", cls: "bg-blue-600 text-white" },
  { id: "inspected", label: "Inspected", cls: "bg-violet-600 text-white" },
  { id: "bought", label: "Bought", cls: "bg-emerald-600 text-white" },
  { id: "lost", label: "Lost", cls: "bg-slate-500 text-white" },
];
const fmt = (n) => (n == null ? "—" : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }));
const VIN_CHECK = {
  verified: { label: "VIN verified", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", Icon: ShieldCheck },
  mismatch: { label: "VIN mismatch", cls: "bg-red-500/20 text-red-300 border-red-500/40", Icon: ShieldAlert },
  invalid: { label: "VIN not recognized", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40", Icon: ShieldAlert },
  none: { label: "No VIN · typed manually", cls: "bg-white/10 text-slate-300 border-white/10", Icon: Keyboard },
  unavailable: { label: "VIN check unavailable", cls: "bg-white/10 text-slate-400 border-white/10", Icon: ShieldQuestion },
};
const VinCheckBadge = ({ lead }) => {
  const c = VIN_CHECK[lead.vin_check];
  if (!c) return null;
  const { Icon } = c;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="vin-check">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold ${c.cls}`} data-testid={`vin-check-${lead.vin_check}`}>
        <Icon className="w-3 h-3" /> {c.label}
      </span>
      {lead.vin_check === "mismatch" && lead.vin_check_detail && (
        <span className="text-[11px] text-red-300" data-testid="vin-check-detail">VIN decodes as {lead.vin_check_detail}</span>
      )}
    </div>
  );
};
const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} hr ago`;
  return `${Math.round(h / 24)} days ago`;
};

export default function AdminLeads() {
  const [key, setKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [leads, setLeads] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [sound, setSound] = useState(() => localStorage.getItem("carfornia_admin_sound") !== "off");
  const [filter, setFilter] = useState("active");
  const knownIds = useRef(null);
  const [flash, setFlash] = useState(new Set());

  const chime = useCallback(() => {
    if (!sound) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [0, 0.18].forEach((t, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.frequency.value = i ? 1318 : 988; o.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t); g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.5); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.55);
      });
    } catch { /* audio blocked */ }
  }, [sound]);

  const load = useCallback(async (k) => {
    const data = await fetchLeadsAdmin(k || key, 200);
    setLeads(data);
    setLastRefresh(new Date());
    const ids = new Set(data.map((l) => l.id));
    if (knownIds.current) {
      const fresh = data.filter((l) => !knownIds.current.has(l.id)).map((l) => l.id);
      if (fresh.length) { setFlash(new Set(fresh)); chime(); setTimeout(() => setFlash(new Set()), 60000); }
    }
    knownIds.current = ids;
  }, [key, chime]);

  const login = async (e) => {
    e?.preventDefault();
    if (!key.trim()) return;
    setChecking(true);
    try {
      await adminPing(key.trim());
      localStorage.setItem(KEY_STORAGE, key.trim());
      setAuthed(true);
      await load(key.trim());
    } catch {
      setAuthed(false);
      localStorage.removeItem(KEY_STORAGE);
      alert("That admin key was not accepted.");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => { if (key) login(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!authed) return undefined;
    const t = setInterval(() => load().catch(() => {}), REFRESH_MS);
    return () => clearInterval(t);
  }, [authed, load]);

  const setStatus = async (lead, status) => {
    const res = await updateLeadStatus(key, lead.id, status);
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status, review_request_sent_at: res.review_request_sent_at || l.review_request_sent_at } : l)));
  };
  const logout = () => { localStorage.removeItem(KEY_STORAGE); setKey(""); setAuthed(false); setLeads([]); knownIds.current = null; };
  const toggleSound = () => { const v = !sound; setSound(v); localStorage.setItem("carfornia_admin_sound", v ? "on" : "off"); };

  const visible = leads.filter((l) => filter === "all" || (filter === "active" ? !["bought", "lost"].includes(l.status) : l.status === filter));
  const newCount = leads.filter((l) => l.status === "new").length;

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-brand-surface px-4" data-testid="admin-login">
        <SEO title="Lead Board — Carfornia Buyers" description="Private" noindex />
        <form onSubmit={login} className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8 w-full max-w-md space-y-4">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Lead Board</h1>
          <p className="text-sm text-slate-600">Private. Enter the admin key once; this computer will stay signed in.</p>
          <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Admin key" className="h-12" data-testid="admin-key-input" autoFocus />
          <Button type="submit" disabled={checking} className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold rounded-xl" data-testid="admin-login-button">
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open lead board"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] text-white" data-testid="admin-leads-board">
      <SEO title="Lead Board — Carfornia Buyers" description="Private" noindex />
      <div className="sticky top-0 z-10 bg-[#0B1220]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
          <h1 className="font-display text-xl font-extrabold">Trade-in leads</h1>
          <span className="px-2.5 py-1 rounded-full bg-brand-orange text-xs font-bold" data-testid="new-count">{newCount} new</span>
          <div className="flex items-center gap-2 text-xs text-slate-400 ml-auto lg:order-last">
            {lastRefresh && <span data-testid="last-refresh" className="hidden sm:inline">Updated {lastRefresh.toLocaleTimeString()} · auto-refresh 30s</span>}
            <button onClick={() => load()} className="p-2 rounded-lg hover:bg-white/10" title="Refresh" data-testid="refresh-button"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={toggleSound} className="p-2 rounded-lg hover:bg-white/10" title="New-lead chime" data-testid="sound-toggle">{sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</button>
            <button onClick={logout} className="p-2 rounded-lg hover:bg-white/10" title="Sign out" data-testid="logout-button"><LogOut className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-wrap gap-1 w-full lg:w-auto lg:ml-2">
            {[["active", "Active"], ["new", "New"], ["contacted", "Contacted"], ["inspected", "Inspected"], ["bought", "Bought"], ["lost", "Lost"], ["all", "All"]].map(([id, label]) => (
              <button key={id} onClick={() => setFilter(id)} data-testid={`filter-${id}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === id ? "bg-white text-slate-900" : "bg-white/10 hover:bg-white/20"}`}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-3">
        {visible.length === 0 && <p className="text-slate-400 py-20 text-center" data-testid="empty-state">No leads in this view.</p>}
        {visible.map((l) => {
          const v = l.valuation;
          const st = STATUSES.find((s) => s.id === l.status) || STATUSES[0];
          const isTest = /test/i.test(`${l.name} ${l.email}`);
          return (
            <div key={l.id} data-testid={`lead-row-${l.id}`} className={`rounded-2xl border p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 transition-colors ${flash.has(l.id) ? "border-brand-orange bg-orange-500/10" : "border-white/10 bg-white/[0.04]"}`}>
              <div className="lg:col-span-3">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase ${st.cls}`} data-testid="lead-status">{st.label}</span>
                  {isTest && <span className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-white/10">TEST</span>}
                  {l.source && <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-white/10 text-slate-300" data-testid="lead-source">{l.source}</span>}
                  <span className="text-xs text-slate-400">{ago(l.created_at)}</span>
                </div>
                <div className="font-display text-xl font-extrabold mt-2" data-testid="lead-vehicle">{[l.year, l.make, l.model].filter(Boolean).join(" ")}</div>
                <div className="text-sm text-slate-300">{l.trim && `${l.trim} · `}{l.mileage ? `${Number(l.mileage).toLocaleString()} mi` : "mileage n/a"} · {l.condition || "condition n/a"}</div>
                <div className="text-xs text-slate-400 mt-1">{l.title_status || "title n/a"} · accidents: {l.accidents || "n/a"}{l.vin && <> · VIN {l.vin}</>}{l.license_plate && <> · plate {l.license_plate} {l.state}</>}</div>
                <VinCheckBadge lead={l} />
                {l.notes && <div className="text-xs text-slate-300 mt-1 italic">“{l.notes}”</div>}
              </div>

              <div className="lg:col-span-3">
                <div className="font-semibold" data-testid="lead-name">{l.name}</div>
                <div className="text-sm text-slate-300">ZIP {l.zip_code}</div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm"><Phone className="w-3.5 h-3.5" /> {l.phone}</a>
                  <a href={`sms:${l.phone}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm"><MessageCircle className="w-3.5 h-3.5" /> Text</a>
                  <a href={`mailto:${l.email}?subject=${encodeURIComponent(`Your ${[l.year, l.make, l.model].filter(Boolean).join(" ")} — Carfornia Buyers`)}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm"><Mail className="w-3.5 h-3.5" /> Email</a>
                </div>
              </div>

              <div className="lg:col-span-3">
                {v ? (
                  <div className="rounded-xl bg-white/5 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">AI appraisal · {v.confidence}</div>
                    <div className="text-sm mt-1">Trade-in <span className="font-bold" data-testid="lead-tradein">{fmt(v.trade_in_low)} – {fmt(v.trade_in_high)}</span></div>
                    <div className="text-sm">Seller saw <span className="font-bold text-brand-orange" data-testid="lead-shown">{fmt(v.offer_low)} – {fmt(v.offer_high)}</span></div>
                    {v.notes && <div className="text-xs text-slate-400 mt-1">{v.notes}</div>}
                  </div>
                ) : (
                  <div className="rounded-xl bg-white/5 p-3 text-sm text-slate-400">{l.valuation_status === "pending" ? "Appraising…" : "No AI appraisal — run manually"}</div>
                )}
                {l.offer_file_url && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${l.offer_file_url}`} target="_blank" rel="noreferrer" data-testid="lead-offer-file" className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg bg-brand-orange/20 text-brand-orange hover:bg-brand-orange/30 text-sm font-semibold"><FileText className="w-3.5 h-3.5" /> View competitor offer</a>
                )}
              </div>

              <div className="lg:col-span-3">
                <div className="flex flex-wrap content-start gap-1.5">
                  {STATUSES.map((s) => (
                    <button key={s.id} onClick={() => setStatus(l, s.id)} data-testid={`set-status-${s.id}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${l.status === s.id ? s.cls : "bg-white/10 hover:bg-white/20"}`}>{s.label}</button>
                  ))}
                </div>
                {l.confirmation_sent_at && (
                  <div className="text-xs text-slate-400 mt-2" data-testid="confirmation-sent">Confirmation emailed to seller {ago(l.confirmation_sent_at)}</div>
                )}
                {l.review_request_sent_at && (
                  <div className="text-xs text-emerald-400 mt-2" data-testid="review-request-sent">Thank-you + Yelp review request emailed {ago(l.review_request_sent_at)}</div>
                )}
                {l.status === "bought" && !l.review_request_sent_at && (
                  <div className="text-xs text-slate-400 mt-2" data-testid="review-request-skipped">No review email sent (test entry or email failed)</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
